import { cn } from "@/lib/utils"
import { AlertCircle, MessageSquare, Users, Activity, RefreshCw } from "lucide-react"

export function AutomotiveProblemsSection() {
  const problems = [
    {
      title: "Missed follow-ups and slow lead response times",
      icon: <AlertCircle className="h-6 w-6" />,
    },
    {
      title: "Disconnected phone, text, chat, and Marketplace conversations",
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      title: "Inconsistent sales process and no playbooks",
      icon: <Users className="h-6 w-6" />,
    },
    {
      title: "No visibility into rep activity or location performance",
      icon: <Activity className="h-6 w-6" />,
    },
    {
      title: "High staff turnover with no repeatable onboarding",
      icon: <RefreshCw className="h-6 w-6" />,
    },
  ]

  const stats = [
    {
      value: "1.5M+",
      label: "AI-assisted lead conversations",
      description: "Handled across six rooftops without losing brand voice.",
    },
    {
      value: "6",
      label: "Stores unified",
      description: "One CRM, inventory feed, and appointment workflow for every rooftop.",
    },
    {
      value: "<10s",
      label: "Average response time",
      description: "AI concierge routes buyers to the right advisor instantly.",
    },
  ]

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        {/* Peader */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="reveal text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            The automotive sector isn't short on leads. It's{" "}
            <span className="text-primary">leaking them.</span>
          </h2>
          <p className="reveal reveal-delay-1 text-xl text-muted-foreground mb-4">
            We fix the operational gaps that kill response times and accountability.
          </p>
          <p className="reveal reveal-delay-2 text-base text-muted-foreground/90">
            The opportunities are already coming in. The problem is the follow-up, the tooling,
            and the lack of a consistent process from BDC to showroom.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 relative z-10 mb-20">
          {problems.map((problem, index) => (
            <div
              key={problem.title}
              className={cn(
                "flex flex-col lg:border-r py-10 relative group/feature border-border",
                index === 0 && "lg:border-l border-border",
                "hover:bg-muted/50 transition-colors duration-200"
              )}
            >
              {/* Hover gradient effect from top */}
              <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-t from-muted/50 to-transparent pointer-events-none" />

              {/* Icon */}
              <div className="mb-4 relative z-10 px-6 text-muted-foreground group-hover/feature:text-primary transition-colors duration-200">
                {problem.icon}
              </div>

              {/* Title with animated bar */}
              <div className="text-sm font-medium relative z-10 px-6">
                <div className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full bg-muted-foreground/30 group-hover/feature:bg-primary transition-all duration-200 origin-center" />
                <span className="group-hover/feature:translate-x-2 transition duration-200 inline-block text-foreground leading-snug">
                  {problem.title}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className="reveal group relative overflow-hidden rounded-3xl bg-white dark:bg-zinc-900 p-8 shadow-lg transition-all duration-300 lift-hover"
              style={{ transitionDelay: `${(index % 4) * 80}ms` }}
            >
              {/* Gradient Glow */}
              <div
                className="absolute bottom-0 left-0 right-0 h-2/3 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                  filter: 'blur(40px)',
                }}
              />

              {/* Bottom Border Glow */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{
                  background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
                  boxShadow: '0 0 15px 3px rgba(47, 95, 232, 0.2)',
                }}
              />

              {/* Content */}
              <div className="relative z-10 text-center">
                <div className="text-5xl md:text-6xl font-bold mb-3 text-[#2F5FE8] dark:text-[#7EA0F5]">
                  {stat.value}
                </div>
                <div className="text-sm font-bold uppercase tracking-wider text-foreground mb-4">
                  {stat.label}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
